import React, { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Portal do colaborador — o que o próprio funcionário consulta.
 *
 * O card do site promete "portal do colaborador para consulta de escalas", e
 * até aqui o professor não tinha tela nenhuma sobre a própria vida funcional:
 * escala, ponto, treinamentos e avaliações eram só do gestor. Somente leitura,
 * de propósito — quem lança ponto e avalia é a gestão.
 */

const dataBR = (v?: string | null) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');
const caixa: React.CSSProperties = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16, marginBottom: 20 };

export default function MeuPainelPage() {
  const [dados, setDados] = useState<any>(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    api.get('/rh/meu-painel')
      .then(({ data }) => setDados(data))
      .catch((e: any) => setErro(e?.response?.data?.message || 'Não foi possível carregar o seu painel.'))
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <div style={{ padding: 24 }}>Carregando…</div>;
  // A mensagem do backend explica o que fazer (vincular o e-mail no cadastro),
  // então ela é mostrada como está — tela em branco viraria chamado.
  if (erro) return <div style={{ padding: 24, color: '#c62828', maxWidth: 640 }}>{erro}</div>;
  if (!dados) return null;

  const { funcionario, ponto, escalas, treinamentos, avaliacoes } = dados;
  const proximas = (escalas || []).slice(0, 10);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ ...caixa, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f1fb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>🧑‍🏫</div>
        <div>
          <h2 style={{ margin: 0 }}>{funcionario.nome}</h2>
          <div style={{ color: '#888', fontSize: 14 }}>Meu painel</div>
        </div>
      </div>

      <section style={caixa}>
        <h3 style={{ marginTop: 0 }}>📆 Minhas escalas do mês</h3>
        {proximas.length === 0 ? (
          <div style={{ color: '#888', fontSize: 14 }}>Nenhuma escala publicada para você neste mês.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {proximas.map((e: any) => (
              <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #f5f5f5', paddingTop: 6 }}>
                <strong style={{ minWidth: 92 }}>{dataBR(e.data)}</strong>
                <span style={{ background: '#eef3f8', borderRadius: 6, padding: '2px 8px', fontSize: 13 }}>{e.turno}</span>
                {e.observacao && <span style={{ color: '#666', fontSize: 13 }}>{e.observacao}</span>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={caixa}>
        <h3 style={{ marginTop: 0 }}>⏱️ Meu ponto no mês</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px', borderLeft: '4px solid #1565c0', paddingLeft: 10 }}>
            <div style={{ color: '#888', fontSize: 13 }}>Total trabalhado</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{ponto?.resumo?.totalFormatado || '0h 00min'}</div>
          </div>
          <div style={{ flex: '1 1 180px', borderLeft: '4px solid #2e7d32', paddingLeft: 10 }}>
            <div style={{ color: '#888', fontSize: 13 }}>Dias fechados</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{ponto?.resumo?.diasCompletos ?? 0}</div>
          </div>
          {Number(ponto?.resumo?.diasIncompletos) > 0 && (
            <div style={{ flex: '1 1 180px', borderLeft: '4px solid #c62828', paddingLeft: 10 }}>
              <div style={{ color: '#888', fontSize: 13 }}>Dias pendentes</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#c62828' }}>{ponto.resumo.diasIncompletos}</div>
              <div style={{ fontSize: 12, color: '#999' }}>fale com a secretaria</div>
            </div>
          )}
        </div>
      </section>

      <section style={caixa}>
        <h3 style={{ marginTop: 0 }}>🎓 Meus treinamentos</h3>
        {(treinamentos || []).length === 0 ? (
          <div style={{ color: '#888', fontSize: 14 }}>Nenhum treinamento registrado.</div>
        ) : treinamentos.map((t: any) => (
          <div key={t.id} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid #f5f5f5', paddingTop: 6, marginTop: 6 }}>
            <strong style={{ flex: '1 1 200px' }}>{t.titulo}</strong>
            <span style={{ color: '#666', fontSize: 13 }}>{t.carga_horaria}h</span>
            {t.valido_ate && <span style={{ fontSize: 13 }}>válido até {dataBR(t.valido_ate)}</span>}
          </div>
        ))}
      </section>

      <section style={caixa}>
        <h3 style={{ marginTop: 0 }}>⭐ Minhas avaliações</h3>
        {(avaliacoes || []).length === 0 ? (
          <div style={{ color: '#888', fontSize: 14 }}>Nenhuma avaliação registrada.</div>
        ) : avaliacoes.map((a: any) => (
          <div key={a.id} style={{ borderTop: '1px solid #f5f5f5', paddingTop: 8, marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ flex: '1 1 160px' }}>{a.periodo}</strong>
              <span style={{ fontSize: 20, fontWeight: 800, color: Number(a.nota_final) >= 4 ? '#2e7d32' : Number(a.nota_final) >= 3 ? '#e65100' : '#c62828' }}>
                {Number(a.nota_final).toFixed(2)}
              </span>
            </div>
            {a.pontos_fortes && <div style={{ fontSize: 13, marginTop: 4 }}>✅ {a.pontos_fortes}</div>}
            {a.pontos_desenvolver && <div style={{ fontSize: 13, color: '#8a6d00' }}>🎯 {a.pontos_desenvolver}</div>}
          </div>
        ))}
      </section>
    </div>
  );
}
